import type { Conversation, ConversationDetails, Message } from './types.js';

const byId = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;
let activeOperatorId = '';

export const refs = {
  loginScreen: byId<HTMLElement>('login-screen'),
  loginForm: byId<HTMLFormElement>('login-form'),
  loginError: byId<HTMLElement>('login-error'),
  desk: byId<HTMLElement>('desk'),
  inbox: byId<HTMLElement>('inbox-view'),
  settings: byId<HTMLElement>('settings-view'),
  list: byId<HTMLElement>('conversation-list'),
  phoneFilter: byId<HTMLInputElement>('phone-filter'),
  siteFilter: byId<HTMLSelectElement>('site-filter'),
  resetFilters: byId<HTMLButtonElement>('reset-filters'),
  emptyChat: byId<HTMLElement>('empty-chat'),
  chatShell: byId<HTMLElement>('chat-shell'),
  messages: byId<HTMLElement>('messages'),
  messageForm: byId<HTMLFormElement>('message-form'),
  messageInput: byId<HTMLTextAreaElement>('message-input'),
  chatName: byId<HTMLElement>('chat-name'),
  chatAvatar: byId<HTMLElement>('chat-avatar'),
  chatPresence: byId<HTMLElement>('chat-presence'),
  chatSite: byId<HTMLElement>('chat-site'),
  closeButton: byId<HTMLButtonElement>('close-button'),
  reopenButton: byId<HTMLButtonElement>('reopen-button'),
  owner: byId<HTMLElement>('conversation-owner'),
  ownerKicker: byId<HTMLElement>('conversation-owner-kicker'),
  ownerLabel: byId<HTMLElement>('conversation-owner-label'),
  joinButton: byId<HTMLButtonElement>('join-conversation'),
  connection: byId<HTMLElement>('connection-indicator'),
  visitorName: byId<HTMLElement>('visitor-name'),
  visitorAvatar: byId<HTMLElement>('visitor-avatar'),
  visitorId: byId<HTMLElement>('visitor-id'),
  visitorUrl: byId<HTMLAnchorElement>('visitor-url'),
  visitorFirst: byId<HTMLElement>('visitor-first'),
  visitorLast: byId<HTMLElement>('visitor-last'),
  visitorLanguage: byId<HTMLElement>('visitor-language'),
  visitorTimezone: byId<HTMLElement>('visitor-timezone'),
  visitorEmail: byId<HTMLElement>('visitor-email'),
  visitorPhone: byId<HTMLElement>('visitor-phone'),
  visitorContactState: byId<HTMLElement>('visitor-contact-state'),
  visitorEditButton: byId<HTMLButtonElement>('visitor-edit-button'),
  visitorProfileShortcut: byId<HTMLButtonElement>('visitor-profile-shortcut'),
  visitorReferrer: byId<HTMLElement>('visitor-referrer'),
  visitorAgent: byId<HTMLElement>('visitor-agent'),
  toast: byId<HTMLElement>('toast')
};

export function showLoggedOut(): void {
  refs.loginScreen.classList.remove('is-hidden');
  refs.desk.classList.add('is-hidden');
}

export function showDesk(): void {
  refs.loginScreen.classList.add('is-hidden');
  refs.desk.classList.remove('is-hidden');
}

export function renderCounts(conversations: Conversation[]): void {
  byId('all-count').textContent = String(conversations.length);
  for (const status of ['NEW', 'OPEN', 'CLOSED'] as const) {
    byId(`${status.toLowerCase()}-count`).textContent = String(conversations.filter((item) => item.status === status).length);
  }
}

export function renderConversationList(
  conversations: Conversation[],
  activeId: string | null,
  onSelect: (id: string) => void,
  emptyMessage = 'Здесь пока тихо. Новые обращения появятся автоматически.'
): void {
  refs.list.replaceChildren();
  if (conversations.length === 0) {
    const empty = document.createElement('p');
    empty.className = 'list-state';
    empty.textContent = emptyMessage;
    refs.list.append(empty);
    return;
  }

  for (const conversation of conversations) {
    const button = document.createElement('button');
    button.className = `conversation-item${conversation.id === activeId ? ' is-active' : ''}`;
    button.type = 'button';
    button.addEventListener('click', () => onSelect(conversation.id));

    const avatarWrap = document.createElement('div');
    avatarWrap.className = `online-ring${conversation.visitorOnline ? ' is-online' : ''}`;
    const avatar = document.createElement('div');
    avatar.className = 'avatar';
    avatar.textContent = initial(conversation.visitorName);
    avatarWrap.append(avatar);

    const copy = document.createElement('div');
    copy.className = 'conversation-copy';
    const name = document.createElement('strong');
    name.textContent = conversation.visitorName;
    const preview = document.createElement('p');
    preview.textContent = conversation.lastMessage || 'Новый диалог';
    copy.append(name, preview);

    const meta = document.createElement('div');
    meta.className = 'conversation-meta';
    const time = document.createElement('time');
    time.dateTime = conversation.lastMessageAt;
    time.textContent = shortTime(conversation.lastMessageAt);
    meta.append(time);
    if (conversation.unreadCount > 0) {
      const unread = document.createElement('span');
      unread.className = 'unread';
      unread.textContent = String(conversation.unreadCount);
      meta.append(unread);
    }
    button.append(avatarWrap, copy, meta);
    refs.list.append(button);
  }
}

export function renderConversation(conversation: ConversationDetails, currentOperatorId: string): void {
  activeOperatorId = currentOperatorId;
  refs.emptyChat.classList.add('is-hidden');
  refs.chatShell.classList.remove('is-hidden');
  refs.inbox.classList.add('has-selection');
  refs.chatName.textContent = conversation.visitorName;
  refs.chatAvatar.textContent = initial(conversation.visitorName);
  refs.chatSite.textContent = conversation.projectName
    ? `${conversation.projectName} · ${conversation.siteName}`
    : conversation.siteName;
  setPresence(conversation.visitorOnline);
  refs.closeButton.classList.toggle('is-hidden', conversation.status === 'CLOSED');
  refs.reopenButton.classList.toggle('is-hidden', conversation.status !== 'CLOSED');
  refs.messageForm.classList.toggle('is-disabled', conversation.status === 'CLOSED');
  refs.messageInput.disabled = conversation.status === 'CLOSED';
  renderOwner(conversation, currentOperatorId);

  refs.messages.replaceChildren();
  let renderedDay = '';
  for (const message of conversation.messages) {
    const day = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long' }).format(new Date(message.createdAt));
    if (day !== renderedDay) {
      const divider = document.createElement('div');
      divider.className = 'day-divider';
      divider.textContent = day;
      refs.messages.append(divider);
      renderedDay = day;
    }
    refs.messages.append(createMessageNode(message));
  }
  refs.messages.scrollTop = refs.messages.scrollHeight;
  renderVisitor(conversation);
}

export function appendMessage(message: Message): void {
  if (refs.messages.querySelector(`[data-message-id="${message.id}"]`)) return;
  refs.messages.append(createMessageNode(message));
  refs.messages.scrollTop = refs.messages.scrollHeight;
}

export function updateConversationHeader(conversation: Conversation, currentOperatorId: string): void {
  activeOperatorId = currentOperatorId;
  refs.chatName.textContent = conversation.visitorName;
  refs.chatSite.textContent = conversation.projectName
    ? `${conversation.projectName} · ${conversation.siteName}`
    : conversation.siteName;
  setPresence(conversation.visitorOnline);
  refs.closeButton.classList.toggle('is-hidden', conversation.status === 'CLOSED');
  refs.reopenButton.classList.toggle('is-hidden', conversation.status !== 'CLOSED');
  refs.messageForm.classList.toggle('is-disabled', conversation.status === 'CLOSED');
  refs.messageInput.disabled = conversation.status === 'CLOSED';
  renderOwner(conversation, currentOperatorId);
}

export function clearConversation(): void {
  refs.inbox.classList.remove('has-selection');
  refs.emptyChat.classList.remove('is-hidden');
  refs.chatShell.classList.add('is-hidden');
  refs.visitorEditButton.disabled = true;
  refs.visitorProfileShortcut.disabled = true;
}

export function toast(message: string): void {
  refs.toast.textContent = message;
  refs.toast.classList.add('is-visible');
  window.setTimeout(() => refs.toast.classList.remove('is-visible'), 2600);
}

function createMessageNode(message: Message): HTMLElement {
  const article = document.createElement('article');
  article.className = `message${message.senderType === 'operator' ? ' message--operator' : ''}${message.senderType === 'system' ? ' message--system' : ''}`;
  article.dataset.messageId = message.id;
  const text = document.createElement('p');
  text.className = 'message__text';
  text.textContent = message.text;
  const meta = document.createElement('div');
  meta.className = 'message__meta';
  const sender = document.createElement('span');
  sender.textContent = message.senderType === 'system'
    ? 'Система'
    : message.senderType === 'visitor'
      ? 'Посетитель'
      : message.senderRole === 'bot'
        ? `ИИ-бот · ${message.senderName ?? 'Suvvy'}`
        : message.senderId === activeOperatorId
          ? 'Вы'
          : message.senderName ?? 'Оператор';
  const time = document.createElement('time');
  time.dateTime = message.createdAt;
  time.textContent = shortTime(message.createdAt);
  meta.append(sender, time);
  article.append(text, meta);
  return article;
}

function renderVisitor(conversation: ConversationDetails): void {
  const visitor = conversation.visitor;
  refs.visitorName.textContent = conversation.visitorName;
  refs.visitorAvatar.textContent = initial(conversation.visitorName);
  refs.visitorId.textContent = conversation.visitorExternalId;
  const currentUrl = visitor?.currentUrl ?? '';
  refs.visitorUrl.textContent = currentUrl || 'Не определена';
  if (currentUrl) refs.visitorUrl.href = currentUrl;
  else refs.visitorUrl.removeAttribute('href');
  refs.visitorFirst.textContent = longTime(visitor?.firstSeenAt);
  refs.visitorLast.textContent = longTime(visitor?.lastSeenAt);
  refs.visitorLanguage.textContent = textValue(visitor?.language, 'Не определён');
  refs.visitorTimezone.textContent = textValue(visitor?.timezone, 'Не определён');
  refs.visitorEmail.textContent = textValue(visitor?.email, 'Не указан');
  refs.visitorPhone.textContent = textValue(visitor?.phone, 'Не указан');
  refs.visitorContactState.textContent = visitor?.contactFormCompletedAt
    ? 'Форма заполнена'
    : visitor?.displayName || visitor?.email || visitor?.phone
      ? 'Карточка дополнена оператором'
      : 'Контакты не запрошены';
  refs.visitorEditButton.disabled = false;
  refs.visitorProfileShortcut.disabled = false;
  refs.visitorReferrer.textContent = textValue(visitor?.referrer, 'Прямой переход');
  refs.visitorAgent.textContent = textValue(visitor?.userAgent, 'Не определён');
}

function setPresence(online: boolean): void {
  refs.chatPresence.textContent = online ? 'Online' : 'Offline';
  document.querySelector('.presence-dot')?.classList.toggle('is-online', online);
}

function renderOwner(conversation: Conversation, currentOperatorId: string): void {
  const mine = conversation.assignedOperatorId === currentOperatorId;
  refs.owner.classList.toggle('is-bot', conversation.assignedOperatorRole === 'bot');
  refs.ownerKicker.textContent = conversation.assignedOperatorRole === 'bot' ? 'Сейчас отвечает ИИ' : 'Ведение диалога';
  refs.ownerLabel.textContent = mine
    ? 'Диалог ведёте вы'
    : conversation.assignedOperatorRole === 'bot'
      ? `ИИ-бот · ${conversation.assignedOperatorName ?? 'Suvvy'}`
      : conversation.assignedOperatorName
        ? `Оператор · ${conversation.assignedOperatorName}`
        : 'Оператор ещё не назначен';
  refs.joinButton.classList.toggle('is-hidden', mine || conversation.status === 'CLOSED');
}

function textValue(value: unknown, fallback: string): string {
  return typeof value === 'string' && value ? value : fallback;
}

function initial(name: string): string {
  return name.trim().charAt(0).toUpperCase() || 'Г';
}

function shortTime(value: string): string {
  const date = new Date(value);
  const today = new Date();
  if (date.toDateString() === today.toDateString()) {
    return new Intl.DateTimeFormat('ru-RU', { hour: '2-digit', minute: '2-digit' }).format(date);
  }
  return new Intl.DateTimeFormat('ru-RU', { day: '2-digit', month: '2-digit' }).format(date);
}

function longTime(value: unknown): string {
  if (typeof value !== 'string') return '—';
  return new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }).format(new Date(value));
}
